import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import type {
  AddResult,
  AnalyzeResult,
  LayoutMapping,
  Settings,
} from '../../lib/settings-types.js';
import { MappingEditor, mappingProblems } from '../../components/MappingEditor.js';
import {
  Button,
  Card,
  Checkbox,
  Combobox,
  Field,
  TextInput,
  Toolbar,
} from '../../components/ui/index.js';
import { errorMessage } from './common.js';

type Step = 'source' | 'review' | 'done';

/**
 * Add stats: paste a provider URL / sheet link or upload a CSV/XLSX. Analyze
 * works out (or recalls) the column mapping; a human confirms it before
 * anything is saved. A reachable URL with no rows yet can be queued as a
 * pending source (retried daily).
 *
 * Laid out as three steps rather than one wrapping grid, because that is what
 * it always was: you cannot review columns before analysing, and the form
 * previously offered every field at once and then grew a second block below it
 * with no way back. The step you're on is named, and Back exists.
 */
/** The host a queued URL points at: the URL itself may carry a token, so it is never printed whole. */
function hostOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return 'the URL';
  }
}

export function AddPane({ settings }: { settings: Settings }) {
  const qc = useQueryClient();
  const [url, setUrl] = useState('');
  const [docsUrl, setDocsUrl] = useState('');
  const [owner, setOwner] = useState('');
  const [label, setLabel] = useState('');
  const [forceGeneric, setForceGeneric] = useState(false);
  const [file, setFile] = useState<{ name: string; b64: string } | null>(null);
  const [analysis, setAnalysis] = useState<AnalyzeResult | null>(null);
  const [mapping, setMapping] = useState<LayoutMapping | null>(null);
  const [done, setDone] = useState<AddResult | null>(null);
  /** A URL queued as a pending source: said here, since the queue itself lives under Email & Sheets (owner report 2026-09-18: "no confirmation, not in the list"). */
  const [queued, setQueued] = useState<{ id: number; url: string; label: string | null } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [importAs, setImportAs] = useState('');
  const [newSource, setNewSource] = useState(false);

  const step: Step = done || queued ? 'done' : analysis ? 'review' : 'source';
  // Only meaningful while "create a new source" is chosen: switching the radio
  // back must not smuggle a leftover name into the request.
  const accountKey = newSource && importAs.trim() ? importAs.trim() : null;
  // A built-in layout isn't sent, so it can't be wrong here.
  const mappingBad =
    analysis?.mode !== 'builtin' && mapping ? mappingProblems(mapping).length > 0 : false;

  // Where this upload will land — resolved BEFORE confirm so a filename that
  // collides with an existing source is a visible choice, not a silent merge.
  const target = useQuery({
    queryKey: ['upload-target', file?.name, owner, accountKey],
    queryFn: () =>
      api.post<{ accountId: string; exists: boolean; lastData: string | null }>(
        '/api/add-source/upload-target',
        {
          filename: file!.name,
          owner,
          ...(accountKey ? { accountKey } : {}),
        },
      ),
    enabled: !!file && !!owner,
  });

  const analyze = useMutation({
    mutationFn: () =>
      api.post<AnalyzeResult>(
        '/api/add-source/analyze',
        file
          ? { data: file.b64, filename: file.name }
          : { url, forceGeneric, docsUrl: docsUrl || undefined },
      ),
    onSuccess: (res) => {
      setError(null);
      setDone(null);
      setAnalysis(res);
      setMapping(res.mapping ? structuredClone(res.mapping) : null);
    },
    onError: (e) => setError(errorMessage(e)),
  });

  const add = useMutation({
    mutationFn: () =>
      api.post<AddResult>(
        '/api/add-source',
        file
          ? {
              owner,
              filename: file.name,
              data: file.b64,
              label: label || undefined,
              mapping: mapping ?? undefined,
              ...(accountKey ? { accountKey } : {}),
            }
          : {
              owner,
              url: analysis?.canonicalUrl ?? url,
              label: label || undefined,
              mapping: analysis?.mode === 'builtin' ? undefined : (mapping ?? undefined),
              forceGeneric,
            },
      ),
    onSuccess: (res) => {
      setError(null);
      setDone(res);
      setAnalysis(null);
      setMapping(null);
      void qc.invalidateQueries({ queryKey: ['settings'] });
      void qc.invalidateQueries({ queryKey: ['summary'] });
    },
    onError: (e) => setError(errorMessage(e)),
  });

  const queuePending = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; id: number }>('/api/pending-sources', {
        url: analysis?.canonicalUrl ?? url,
        owner: owner || undefined,
        label: label || undefined,
      }),
    onSuccess: (res) => {
      setQueued({ id: res.id, url: analysis?.canonicalUrl ?? url, label: label || null });
      setAnalysis(null);
      setDone(null);
      setError(null);
      void qc.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (e) => setError(errorMessage(e)),
  });

  const onFile = (f: File | undefined) => {
    if (!f) {
      setFile(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setFile({ name: f.name, b64: dataUrl.slice(dataUrl.indexOf(',') + 1) });
      setUrl(''); // a file and a URL are alternatives, in both directions
    };
    reader.readAsDataURL(f);
  };

  const ownerOptions = [
    ...settings.users.map((u) => u.username),
    ...settings.reps.filter(
      (r) => !settings.users.some((u) => u.username.toLowerCase() === r.toLowerCase()),
    ),
  ].map((o) => ({ value: o, label: o }));

  const reset = () => {
    setUrl('');
    setDocsUrl('');
    setLabel('');
    setFile(null);
    setImportAs('');
    setNewSource(false);
    setAnalysis(null);
    setMapping(null);
    setDone(null);
    setQueued(null);
    setError(null);
  };

  return (
    <div>
      <ol className="ui-steps" aria-label="Progress">
        <li aria-current={step === 'source' ? 'step' : undefined}>1. Source</li>
        <li aria-current={step === 'review' ? 'step' : undefined}>2. Review columns</li>
        <li aria-current={step === 'done' ? 'step' : undefined}>3. Import</li>
      </ol>

      {step === 'source' ? (
        <Card
          title="Where do the stats come from?"
          description="A provider stats URL (Xaria, AEMDays and JustForSpends are recognised automatically), a Google Sheets link, or a CSV/XLSX file. You confirm the column matching before anything is imported."
        >
          <div className="cfg-grid">
            <Field
              label="Stats URL"
              hint={file ? 'Clear the file below to use a URL instead.' : undefined}
            >
              {(aria) => (
                <TextInput
                  {...aria}
                  value={url}
                  placeholder="https://…"
                  disabled={!!file}
                  onChange={(e) => setUrl(e.target.value)}
                />
              )}
            </Field>

            <Field label="…or upload a report" hint="CSV, XLSX or XLSM.">
              {(aria) => (
                <Toolbar>
                  <input
                    {...aria}
                    type="file"
                    accept=".csv,.xlsx,.xlsm"
                    disabled={!!url}
                    onChange={(e) => onFile(e.target.files?.[0])}
                  />
                  {file ? (
                    <Button variant="link" size="sm" onClick={() => setFile(null)}>
                      Clear
                    </Button>
                  ) : null}
                </Toolbar>
              )}
            </Field>

            <Field label="Owner" required hint="Whose numbers these are on the dashboard.">
              {(aria) => (
                <Combobox
                  {...aria}
                  value={owner}
                  onChange={setOwner}
                  options={ownerOptions}
                  placeholder="pick an owner"
                />
              )}
            </Field>

            <Field label="Name" hint="Optional — what to call this source in the app.">
              {(aria) => (
                <TextInput {...aria} value={label} onChange={(e) => setLabel(e.target.value)} />
              )}
            </Field>

            {!file ? (
              <Field
                label="Provider docs URL"
                hint="Optional. Helps the column matcher understand an unfamiliar report."
              >
                {(aria) => (
                  <TextInput
                    {...aria}
                    value={docsUrl}
                    placeholder="https://…"
                    onChange={(e) => setDocsUrl(e.target.value)}
                  />
                )}
              </Field>
            ) : null}
          </div>

          {!file ? (
            <div className="cfg-stack">
              <Checkbox
                label="Customise columns even for a recognised provider"
                checked={forceGeneric}
                onChange={(e) => setForceGeneric(e.target.checked)}
              />
            </div>
          ) : null}

          <Toolbar className="cfg-actions">
            <Button
              onClick={() => analyze.mutate()}
              loading={analyze.isPending}
              disabled={(!url && !file) || !owner}
            >
              Analyze
            </Button>
            {!owner ? <span className="ui-field__hint">Pick an owner to continue.</span> : null}
          </Toolbar>
          {error ? <p className="ui-field__error">{error}</p> : null}
        </Card>
      ) : null}

      {step === 'review' && analysis ? (
        analysis.mode === 'empty' ? (
          <Card title="Nothing to import yet">
            <p className="table-note">
              The URL answers but has no rows yet. Queue it as a pending source — it is retried
              daily and waits for your confirmation when data appears.
            </p>
            <Toolbar className="cfg-actions">
              <Button onClick={() => queuePending.mutate()} loading={queuePending.isPending}>
                Queue as pending source
              </Button>
              <Button variant="ghost" onClick={() => setAnalysis(null)}>
                Back
              </Button>
            </Toolbar>
            {error ? <p className="ui-field__error">{error}</p> : null}
          </Card>
        ) : (
          <Card
            title="Review the columns"
            description={
              analysis.mode === 'builtin'
                ? `Recognised as ${analysis.system} — the built-in field mapping below is applied.`
                : analysis.mode === 'template'
                  ? 'A layout for this source is already learned — review and confirm.'
                  : 'Proposed column matching (one-time learn) — review and confirm.'
            }
          >
            {mapping ? (
              <MappingEditor
                columns={analysis.columns ?? []}
                samples={analysis.samples ?? {}}
                mapping={mapping}
                onChange={setMapping}
                readOnly={analysis.mode === 'builtin'}
              />
            ) : null}

            {file && owner && target.data ? (
              target.data.exists ? (
                <div className="cfg-callout">
                  <p>
                    A source called <b>{target.data.accountId}</b> already exists
                    {target.data.lastData ? ` (last data ${target.data.lastData})` : ''}. Days this
                    file restates would replace that source&apos;s rows.
                  </p>
                  <label className="ui-check">
                    <input
                      type="radio"
                      name="merge"
                      checked={!newSource}
                      onChange={() => setNewSource(false)}
                    />
                    <span>
                      Import into <b>{target.data.accountId}</b>
                    </span>
                  </label>
                  <label className="ui-check">
                    <input
                      type="radio"
                      name="merge"
                      checked={newSource}
                      onChange={() => setNewSource(true)}
                    />
                    <span>This is a different report — create a new source</span>
                  </label>
                  {newSource ? (
                    <Field label="New source name" required>
                      {(aria) => (
                        <TextInput
                          {...aria}
                          value={importAs}
                          placeholder="e.g. partner-b-weekly"
                          onChange={(e) => setImportAs(e.target.value)}
                        />
                      )}
                    </Field>
                  ) : null}
                </div>
              ) : (
                <p className="table-note">
                  Will create source <b>{target.data.accountId}</b>.
                </p>
              )
            ) : null}

            <Toolbar className="cfg-actions">
              <Button
                onClick={() => add.mutate()}
                loading={add.isPending}
                disabled={(newSource && !importAs.trim()) || mappingBad}
              >
                Confirm &amp; import
              </Button>
              <Button variant="ghost" onClick={() => setAnalysis(null)}>
                Back
              </Button>
            </Toolbar>
            {error ? <p className="ui-field__error">{error}</p> : null}
          </Card>
        )
      ) : null}

      {step === 'done' && queued ? (
        <Card title="Queued as a pending source">
          <div className="warnbox ok">
            <b>{queued.label ?? hostOf(queued.url)}</b> is queued. Nothing is imported until the URL
            answers with rows: it is checked once a day, or now with <b>Check now</b> under{' '}
            <a href="/configuration/ingest">Configuration → Email &amp; Sheets → Queued URLs</a>,
            where it is listed with its last check. When rows appear, its layout waits for your
            confirmation here under Add stats.
          </div>
          <Toolbar className="cfg-actions">
            <Button onClick={reset}>Add another source</Button>
          </Toolbar>
        </Card>
      ) : null}
      {step === 'done' && done ? (
        <Card title="Imported">
          <div className="warnbox ok">
            Imported {done.rows} row{done.rows === 1 ? '' : 's'} into <b>{done.accountId}</b>
            {done.learned ? ' (layout learned)' : ''}
            {done.warning ? ` — warning: ${done.warning}` : ''}
          </div>
          <Toolbar className="cfg-actions">
            <Button onClick={reset}>Add another source</Button>
          </Toolbar>
        </Card>
      ) : null}
    </div>
  );
}
